/* ============================================================================
   PerformanceSection / index.js
   Gráfico de desempenho do aluno por matéria ao longo do ano.
   SEÇÃO EMBUTIDA no dashboard (sempre visível, abaixo dos botões de ação).

   Pensado para PAIS NO CELULAR:
     - uma matéria por vez (chips roláveis) → a linha fica grande e legível,
       em vez de 8 linhas sobrepostas virando espaguete
     - NOTA: normalizada em % da nota total do período. Isso torna os períodos
       comparáveis mesmo se a escala mudar (ex.: 25 num bimestre, 30 noutro)
     - CONCEITO: A/B/C/D vira escala ordinal (4→1) e o eixo mostra as LETRAS,
       não números — "B" o pai entende, "3.0" não
     - tendência (subiu/caiu) calculada do 1º ao último período com dado

   Uso no dashboard (abaixo da ActionsSection):
     import PerformanceSection from '../../components/SecaoDesempenho'

     <PerformanceSection
       studentId={studentId}
       format={assessmentFormatOf(student)}   // 'grade' | 'concept'
       idSchool={student?.id_school?._id}
     />

   Não renderiza nada enquanto não houver notas lançadas (fica invisível),
   para não poluir o dashboard no começo do ano.
   ========================================================================== */

   import React, { useState, useEffect, useRef, useMemo } from 'react'
   import {
       ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis,
       CartesianGrid, Tooltip, ReferenceLine, Cell,
   } from 'recharts'
   import { FiTrendingUp, FiTrendingDown, FiMinus, FiBarChart2 } from 'react-icons/fi'
   import { getReportCardsByStudentAndYear, getSchoolYear } from '../../Api'
   import { useRegimeDoAno } from '../../utils/regimeDoAno'
   import {
       Section, SectionHead, SectionTitle, SectionSub,
       Body, Loading,
       ChipsWrap, Chip, ChipDot,
       ChartPanel, ChartHead, ChartTitle, TrendBadge, ChartArea,
       TipBox, TipLabel, TipMeta,
       SummaryTitle, SummaryList, SummaryItem, SummaryName, SummaryRight,
       SummaryValue, Spark, FootNote,
   } from './styles'
   
   /* ---------- helpers --------------------------------------------------------- */
   
   //const TEAL = '#0f6e52'
   const TEAL_DEEP = '#0a4d3a'   // barra do "Final" (destaque)
   const TEAL_BAR = '#7fc9ae'    // barras dos períodos (tom claro, a linha se destaca)
   const LINE = '#0f6e52'        // linha sobreposta
   const GREEN = '#1d8b5a'
   const RED = 'var(--red, #dc2626)'
   const AMBER = '#b7791f'
   
   const norm = (s = '') => String(s).trim().toUpperCase()
   const EF_NAME = 'EDUCAÇÃO FÍSICA'
   
   const bimIndex = (b) => {
       const m = String(b || '').match(/\d+/)
       return m ? parseInt(m[0], 10) - 1 : 99
   }
   
   const toNumber = (v) => {
       if (v == null || v === '') return NaN
       if (typeof v === 'number') return v
       return parseFloat(String(v).replace(',', '.'))
   }
   
   // studentGrade: nota = { total, atividades } · conceito = "B" · histórico = { value }
   const cellValue = (cell, format) => {
       if (cell == null) return ''
       if (typeof cell === 'object') {
           if ('total' in cell) return cell.total
           if ('value' in cell) return cell.value
           return ''
       }
       if (format === 'concept') return String(cell).split(',')[0].trim()
       return cell
   }
   
   // conceitos viram escala ordinal para a linha fazer sentido (A no topo)
   const CONCEPT_SCALE = { A: 4, B: 3, C: 2, D: 1 }
   const CONCEPT_LABEL = { 4: 'A', 3: 'B', 2: 'C', 1: 'D' }
   
   // Três status de tendência. A tolerância existe porque, sem ela, uma variação
   // mínima (0,5%) já marcaria "evoluindo" — e "Estável" nunca apareceria.
   const TOL_PCT = 3        // notas: variação menor que 3 p.p. = estável
   const TOL_CONCEPT = 0    // conceito: escala discreta, mesma letra = estável
   
   const statusOf = (trend, isConcept) => {
       const tol = isConcept ? TOL_CONCEPT : TOL_PCT
       if (trend > tol) return 'up'
       if (trend < -tol) return 'down'
       return 'flat'
   }
   
   // transforma os ReportCards em séries por matéria
   const buildSeries = (cards, format, finalConcepts, regimeAno = '') => {
       const list = Array.isArray(cards) ? cards : []
       const isConcept = format === 'concept'
   
       // conceito final por matéria (vem do backend; não é calculável)
       const finalMap = new Map()
       for (const fc of (Array.isArray(finalConcepts) ? finalConcepts : [])) {
           const k = norm(fc?.matter)
           if (k) finalMap.set(k, String(fc?.concept || '').trim())
       }
   
       // regime a partir do 1º período (qualquer quarter)
       let first = null, fi = Infinity
       for (const c of list) {
           const i = bimIndex(c?.bimonthly)
           if (i >= 0 && i < fi) { fi = i; first = c }
       }
       const qOf = (c) => c?.id_iStQuarter || c?.id_iiNdQuarter || c?.id_iiiRdQuarter || c?.id_ivThQuarter
       let regime = String(qOf(first)?.assessmentRegime || '').trim().toUpperCase()
       if (!regime) {
           for (const c of list) {
               const r = qOf(c)?.assessmentRegime
               if (r) { regime = String(r).trim().toUpperCase(); break }
           }
       }
       // regime do ANO (vem da escola); sem ele, usa o que vier nos dados
       if (regimeAno) regime = String(regimeAno).trim().toUpperCase()
       const isTri = regime === 'TRIMESTRAL'
       const label = isTri ? 'Trim' : 'Bim'
       const maxIdx = isTri ? 2 : 3
   
       // escala (total/média) de cada período — usada para normalizar as notas
       const scale = {}
       for (const c of list) {
           const i = bimIndex(c?.bimonthly)
           if (i < 0 || i > maxIdx) continue
           scale[i] = { total: toNumber(c?.totalGrade), average: toNumber(c?.averageGrade) }
       }
   
       const periods = new Set()
       const mm = new Map()
       for (const c of list) {
           const i = bimIndex(c?.bimonthly)
           if (i < 0 || i > maxIdx) continue
           periods.add(i)
           const sg = c?.studentGrade || {}
           const entries = sg instanceof Map ? [...sg.entries()] : Object.entries(sg)
           for (const [matter, raw] of entries) {
               const k = norm(matter)
               if (!mm.has(k)) mm.set(k, { matter, vals: {} })
               const v = cellValue(raw, format)
               mm.get(k).vals[i] = isConcept
                   ? (CONCEPT_SCALE[String(v).toUpperCase()[0]] ?? null)
                   : toNumber(v)
           }
       }
       const periodList = [...periods].sort((a, b) => a - b)
   
       const matters = [...mm.values()]
           .map((m) => {
               const data = periodList.map((i) => {
                   const v = m.vals[i]
                   const sc = scale[i] || {}
                   // nota → % do total daquele período (comparável entre períodos)
                   const pct = (!isConcept && v != null && !isNaN(v) && !isNaN(sc.total) && sc.total > 0)
                       ? Math.round((v / sc.total) * 1000) / 10
                       : null
                   const val = (v == null || isNaN(v)) ? null : v
                   return {
                       periodo: `${i + 1}º ${label}`,
                       // o eixo Y plota % (nota) ou o ordinal (conceito)
                       y: isConcept ? val : pct,
                       valor: val,
                       pct,
                       media: isNaN(sc.average) ? null : sc.average,
                       total: isNaN(sc.total) ? null : sc.total,
                       mediaPct: (!isConcept && !isNaN(sc.average) && !isNaN(sc.total) && sc.total > 0)
                           ? Math.round((sc.average / sc.total) * 1000) / 10
                           : null,
                   }
               })
   
               const pts = data.filter((d) => d.y != null)
               const trend = pts.length >= 2 ? (pts[pts.length - 1].y - pts[0].y) : 0
               const last = pts.length ? pts[pts.length - 1] : null
               const status = statusOf(trend, isConcept)
   
               // ── RESULTADO FINAL ──
               // NOTA: soma das notas ÷ soma dos totais dos períodos → %
               // CONCEITO: vem de finalConcepts (o professor define; não é calculável)
               let final = null
               if (isConcept) {
                   const c = finalMap.get(norm(m.matter))
                   const ord = c ? CONCEPT_SCALE[String(c).toUpperCase()[0]] : null
                   if (ord) {
                       final = {
                           periodo: 'Final',
                           isFinal: true,
                           y: ord,
                           valor: ord,
                           conceito: String(c).toUpperCase()[0],
                           pct: null,
                           media: null,
                           total: null,
                       }
                   }
               } else {
                   let somaNota = 0, somaTotal = 0, any = false
                   for (const i of periodList) {
                       const v = m.vals[i]
                       const sc = scale[i] || {}
                       if (v != null && !isNaN(v)) { somaNota += v; any = true }
                       if (!isNaN(sc.total)) somaTotal += sc.total
                   }
                   if (any && somaTotal > 0) {
                       final = {
                           periodo: 'Final',
                           isFinal: true,
                           y: Math.round((somaNota / somaTotal) * 1000) / 10,
                           valor: Math.round(somaNota * 10) / 10,
                           total: Math.round(somaTotal * 10) / 10,
                           pct: Math.round((somaNota / somaTotal) * 1000) / 10,
                           media: null,
                       }
                   }
               }
   
               // a barra do "Final" entra no fim do gráfico, destacada
               const chartData = final ? [...data, final] : data
   
               return { matter: m.matter, data: chartData, trend, status, last, final, pontos: pts.length }
           })
           // alfabético com Educação Física no fim (padrão dos boletins)
           .sort((a, b) => {
               const aEF = norm(a.matter) === EF_NAME
               const bEF = norm(b.matter) === EF_NAME
               if (aEF && !bEF) return 1
               if (bEF && !aEF) return -1
               return a.matter.localeCompare(b.matter, 'pt-BR')
           })
           .filter((m) => m.pontos > 0)
   
       // linha da média (só nota) — referência visual de "o que é suficiente"
       const mediaPct = (() => {
           for (const i of periodList) {
               const sc = scale[i] || {}
               if (!isNaN(sc.average) && !isNaN(sc.total) && sc.total > 0) {
                   return Math.round((sc.average / sc.total) * 1000) / 10
               }
           }
           return null
       })()
   
       return { matters, periodList, label, isConcept, isTri, mediaPct }
   }
   
   const fmtValue = (d, isConcept) => {
       if (!d || d.y == null) return '—'
       if (isConcept) return CONCEPT_LABEL[d.valor] || '—'
       return `${d.pct}%`
   }
   
   /* ---------- componente ------------------------------------------------------ */
   
   const PerformanceSection = ({
       studentId,
       format = 'grade',
       idSchool,
   }) => {
       const [year, setYear] = useState(new Date().getFullYear())
       // ano letivo atual da escola — teto do seletor de anos
       const [anoLetivoAtual, setAnoLetivoAtual] = useState(new Date().getFullYear())
       const [cards, setCards] = useState([])
       const [finalConcepts, setFinalConcepts] = useState([])
       const [loading, setLoading] = useState(false)
       const [selected, setSelected] = useState(null)
       const cancelled = useRef(false)
   
       const isConcept = format === 'concept'
   
       const schoolId = (() => {
           if (idSchool) return idSchool
           try {
               const s = JSON.parse(sessionStorage.getItem('id-school'))
               if (s) return s
           } catch (e) { /* ignora */ }
           return null
       })()
   
       // ano letivo da escola (fallback: ano atual)
       useEffect(() => {
           let active = true
           ;(async () => {
               if (!schoolId) { if (active) setYear(new Date().getFullYear()); return }
               try {
                   const res = await getSchoolYear(schoolId)
                   if (!active) return
                   const y = parseInt(res?.data?.data, 10)
                   const anoValido = !isNaN(y) ? y : new Date().getFullYear()
                   setYear(anoValido)
                   setAnoLetivoAtual(anoValido) // teto do seletor
               } catch (e) {
                   if (active) setYear(new Date().getFullYear())
               }
           })()
           return () => { active = false }
       }, [schoolId])
   
       // busca os boletins do ano
       // (cada busca tem a sua própria trava: uma resposta antiga nunca apaga a mais nova)
       useEffect(() => {
           if (!studentId) return
           let ativo = true
           cancelled.current = false
           setLoading(true)
           const load = async () => {
               try {
                   const res = await getReportCardsByStudentAndYear(studentId, year)
                   if (!ativo) return
                   setCards(res?.data?.data || [])
                   setFinalConcepts(res?.data?.finalConcepts || [])
                   setSelected(null)
               } catch (err) {
                   if (!ativo) return
                   setCards([])
                   setFinalConcepts([])
               } finally {
                   if (ativo) setLoading(false)
               }
           }
           load()
           return () => { ativo = false; cancelled.current = true }
       }, [year, studentId])

       // regime de avaliação do ANO exibido (bimestre ou trimestre)
       const regimeAno = useRegimeDoAno(schoolId, year)
   
       const { matters, label, mediaPct } = useMemo(
           () => buildSeries(cards, format, finalConcepts, regimeAno),
           [cards, format, finalConcepts, regimeAno]
       )
   
       const hasData = matters.length > 0
   
       // enquanto carrega, mostra o esqueleto; sem dados, não renderiza nada
       // (não polui o dashboard no começo do ano letivo) — MAS só quando o
       // usuário está no ano letivo atual. Se ele navegou para outro ano,
       // mantemos a seção visível para não "prender" o seletor de ano.
       const noAnoAtual = year === anoLetivoAtual
       if (!loading && !hasData && noAnoAtual) return null
       const active = matters.find((m) => m.matter === selected) || matters[0]
   
       // três status de tendência
       const trendInfo = (st) => {
           if (st === 'up') return { icon: <FiTrendingUp size={14} />, text: 'Evoluindo' }
           if (st === 'down') return { icon: <FiTrendingDown size={14} />, text: 'Em queda' }
           return { icon: <FiMinus size={14} />, text: 'Estável' }
       }
   
       // tooltip que fala a língua dos pais
       const CustomTip = ({ payload, label: lbl }) => {
           if (!payload || !payload.length) return null
           const d = payload[0].payload
           if (d.y == null) return null
           return (
               <TipBox>
                   <TipLabel>{d.isFinal ? 'Resultado final' : lbl}</TipLabel>
                   <strong>
                       {isConcept
                           ? (d.isFinal ? d.conceito : CONCEPT_LABEL[d.valor])
                           : `${d.valor}${d.total ? ` / ${d.total}` : ''}`}
                   </strong>
                   {!isConcept && d.pct != null && (
                       <TipMeta>
                           {d.pct}% {d.isFinal ? 'do total do ano' : 'da nota total'}
                       </TipMeta>
                   )}
                   {!isConcept && !d.isFinal && d.media != null && (
                       <TipMeta>Média do período: {d.media}</TipMeta>
                   )}
                   {isConcept && d.isFinal && (
                       <TipMeta>Conceito final do ano</TipMeta>
                   )}
               </TipBox>
           )
       }
   
       // domínio e ticks do eixo Y
       const yDomain = isConcept ? [0.5, 4.5] : [0, 100]
       const yTicks = isConcept ? [1, 2, 3, 4] : [0, 25, 50, 75, 100]
       const yFormat = (v) => (isConcept ? (CONCEPT_LABEL[v] || '') : `${v}%`)
   
       // sparkline das demais matérias
       const sparkPath = (m) => {
           const pts = m.data.filter((d) => d.y != null)
           if (pts.length < 2) return null
           const min = isConcept ? 1 : 0
           const max = isConcept ? 4 : 100
           const w = 46, h = 20
           const step = w / (pts.length - 1)
           return pts
               .map((d, i) => {
                   const x = i * step
                   const y = h - ((d.y - min) / (max - min)) * h
                   return `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${Math.max(1, Math.min(h - 1, y)).toFixed(1)}`
               })
               .join(' ')
       }
   
       return (
           <Section>
               <SectionHead>
                   <SectionTitle>
                       <FiBarChart2 size={18} /> Desempenho no ano
                   </SectionTitle>
                   <SectionSub>
                       <label style={{ display: 'flex', alignItems: 'center', gap: 8, margin: 0 }}>
                           <span>Ano letivo</span>
                           <select
                               value={year}
                               onChange={(e) => setYear(Number(e.target.value))}
                               style={{
                                   padding: '4px 8px',
                                   borderRadius: 6,
                                   border: '1.5px solid var(--line)',
                                   fontSize: 14,
                                   fontWeight: 600,
                                   fontFamily: 'inherit',
                                   color: 'var(--teal-text, var(--teal))',
                                   background: 'var(--surface)',
                                   cursor: 'pointer',
                                   outline: 'none'
                               }}
                           >
                               {Array.from({ length: 6 }, (_, i) => anoLetivoAtual - i).map((y) => (
                                   <option key={y} value={y}>
                                       {y}{y === anoLetivoAtual ? ' (atual)' : ''}
                                   </option>
                               ))}
                           </select>
                       </label>
                   </SectionSub>
               </SectionHead>
   
               <Body>
                   {loading ? (
                       <Loading>Carregando desempenho…</Loading>
                       ) : !hasData ? (
                           <Loading>Ainda não há dados de desempenho para {year}.</Loading>
                       ) : (
                           <>
                               {/* seletor de matéria — rolável no mobile */}
                               <ChipsWrap>
                                   {matters.map((m) => (
                                       <Chip
                                           key={m.matter}
                                           type="button"
                                           $active={active?.matter === m.matter}
                                           onClick={() => setSelected(m.matter)}
                                       >
                                           <ChipDot $status={m.status} />
                                           {m.matter}
                                       </Chip>
                                   ))}
                               </ChipsWrap>
   
                               {/* gráfico da matéria selecionada */}
                               {active && (
                                   <ChartPanel>
                                       <ChartHead>
                                           <ChartTitle>{active.matter}</ChartTitle>
                                           <TrendBadge $status={active.status}>
                                               {trendInfo(active.status).icon}
                                               {trendInfo(active.status).text}
                                           </TrendBadge>
                                       </ChartHead>
   
                                       <ChartArea>
                                           <ResponsiveContainer width="100%" height="100%">
                                               <ComposedChart
                                                   data={active.data}
                                                   margin={{ top: 12, right: 14, left: -12, bottom: 4 }}
                                                   barCategoryGap="22%"
                                               >
                                                   <CartesianGrid
                                                       strokeDasharray="3 3"
                                                       stroke="#eef2f0"
                                                       vertical={false}
                                                   />
                                                   <XAxis
                                                       dataKey="periodo"
                                                       tick={{ fontSize: 11.5, fill: '#566b62' }}
                                                       axisLine={{ stroke: '#e0e8e4' }}
                                                       tickLine={false}
                                                       interval={0}
                                                   />
                                                   <YAxis
                                                       domain={yDomain}
                                                       ticks={yTicks}
                                                       tickFormatter={yFormat}
                                                       tick={{ fontSize: 12, fill: '#566b62' }}
                                                       axisLine={false}
                                                       tickLine={false}
                                                       width={44}
                                                   />
                                                   <Tooltip
                                                       content={<CustomTip />}
                                                       cursor={{ fill: 'rgba(15, 110, 82, 0.06)' }}
                                                   />
   
                                                   {/* linha da média — só faz sentido em nota */}
                                                   {!isConcept && mediaPct != null && (
                                                       <ReferenceLine
                                                           y={mediaPct}
                                                           stroke={AMBER}
                                                           strokeDasharray="5 5"
                                                           label={{
                                                               value: 'média',
                                                               position: 'right',
                                                               fill: AMBER,
                                                               fontSize: 10,
                                                               fontWeight: 700,
                                                           }}
                                                       />
                                                   )}
   
                                                   {/* BARRAS — a do "Final" sai destacada (mais escura) */}
                                                   <Bar
                                                       dataKey="y"
                                                       radius={[7, 7, 0, 0]}
                                                       maxBarSize={54}
                                                       isAnimationActive={false}
                                                   >
                                                       {active.data.map((d, i) => (
                                                           <Cell
                                                               key={i}
                                                               fill={d.isFinal ? TEAL_DEEP : TEAL_BAR}
                                                           />
                                                       ))}
                                                   </Bar>
   
                                                   {/* LINHA sobreposta — nasce no zero e passa por cima das barras */}
                                                   <Line
                                                       type="monotone"
                                                       dataKey="y"
                                                       stroke={LINE}
                                                       strokeWidth={2.5}
                                                       dot={{ r: 4, fill: '#fff', stroke: LINE, strokeWidth: 2 }}
                                                       activeDot={{ r: 6, fill: LINE, stroke: '#fff', strokeWidth: 2 }}
                                                       connectNulls
                                                       isAnimationActive={false}
                                                   />
                                               </ComposedChart>
                                           </ResponsiveContainer>
                                       </ChartArea>
                                   </ChartPanel>
                               )}
   
                               {/* resumo das demais matérias, com mini-sparkline */}
                               {matters.length > 1 && (
                                   <>
                                       <SummaryTitle>Todas as matérias</SummaryTitle>
                                       <SummaryList>
                                           {matters.map((m) => {
                                               const d = sparkPath(m)
                                               const tone = m.status
                                               const color = m.status === 'up' ? GREEN : m.status === 'down' ? RED : AMBER
                                               return (
                                                   <SummaryItem
                                                       key={m.matter}
                                                       type="button"
                                                       $active={active?.matter === m.matter}
                                                       onClick={() => setSelected(m.matter)}
                                                   >
                                                       <SummaryName title={m.matter}>{m.matter}</SummaryName>
                                                       <SummaryRight>
                                                           {d && (
                                                               <Spark viewBox="0 0 46 20" aria-hidden="true">
                                                                   <path
                                                                       d={d}
                                                                       fill="none"
                                                                       stroke={color}
                                                                       strokeWidth="2"
                                                                       strokeLinecap="round"
                                                                       strokeLinejoin="round"
                                                                   />
                                                               </Spark>
                                                           )}
                                                           <SummaryValue $tone={tone}>
                                                               {m.final
                                                                   ? (isConcept ? m.final.conceito : `${m.final.pct}%`)
                                                                   : fmtValue(m.last, isConcept)}
                                                           </SummaryValue>
                                                       </SummaryRight>
                                                   </SummaryItem>
                                               )
                                           })}
                                       </SummaryList>
                                   </>
                               )}
   
                               <FootNote>
                                   {isConcept
                                       ? `Conceitos por ${label === 'Trim' ? 'trimestre' : 'bimestre'} · A (melhor) a D`
                                       : `Notas em % da nota total de cada ${label === 'Trim' ? 'trimestre' : 'bimestre'} — assim os períodos ficam comparáveis`}
                               </FootNote>
                           </>
                       )}
               </Body>
           </Section>
       )
   }
   
   export default PerformanceSection